// ============================================================================
// ui/showcase.js — "Show me what's possible" intro parade (Fable §3.1, batch B5)
// ============================================================================
// A single glass pill in the Logo pane invites a curated parade of 3–4
// art-directed FICTIONAL identities (showcase/identities.js). One plate is on
// stage at a time — the persistent 3D plate's own idle SWAY is the "oscillating"
// motion (no user orbit needed) — with side arrows to flip and a TOP header (org
// name + dots) above the model. The pane pill itself is the exit: while the parade
// is open it morphs into "Design your own" (Ryan 2026-07-17). Nothing sits in the
// bottom band, so the parade never overlaps the product switcher bar there.
//
// PRELOAD DISCIPLINE (§3.1 / §10): every identity is rendered to a cached raster
// BEFORE the chip enables (chip shows a shimmer until warm), so entry + every flip
// is jank-free. Entry plays the dramatic reveal (a taste of the set-piece); flips
// crossfade. Reduced motion → crossfades only.
//
// Drives the plate through the ctx.stage API (main.js) so the user's real (blank)
// design underneath is never touched — leaving the showcase restores it.
// ============================================================================

import { h } from './dom.js';
import { layoutFamilyFor, productSupportsBodyColor, modelFor } from '../products.js';

export function createShowcase(ctx, opts = {}) {
  // onActiveChange(active) fires as the parade opens/closes so the host can clear
  // the top-right corner: the undo/redo/zoom cluster slides out while the parade's
  // own 'x' owns that corner, then slides back (main.js — 2026-07-16, Ryan).
  const { onActiveChange, onStartDesign } = opts;
  let items = null;        // [{ id, name, caption, raster }]
  let preloading = false;
  let ready = false;
  let stageReady = false;
  let active = false;
  let index = 0;
  // Resolves once the parade assets are warm — lets a caller (the landing
  // gate's "Show me what's possible" choice) auto-enter as soon as it's safe,
  // without polling `ready` itself (2026-07-14).
  let resolveReady = null;
  /* Until when the background slide build must not start another raster — see its use in `preload`.
     Set by the two moments that own the screen: opening the parade and leaving it. A timestamp rather
     than a boolean so a build already waiting cannot miss the window being opened again. */
  let quietUntil = 0;
  const holdBuild = (ms) => {
    const now = typeof performance !== 'undefined' ? performance.now() : 0;
    quietUntil = Math.max(quietUntil, now + ms);
  };
  const readyPromise = new Promise((res) => { resolveReady = res; });

  /* A tap that arrived before the set was showable. Honoured by `preload` the moment the first slide
     rasters — see the chip's own click handler for what it costs to drop one instead. */
  let pendingEnter = false;

  // ── the pane pill: "Show me what's possible" ↔ "Design your own" ────────────
  // ONE pill, mounted in the Logo pane above the dropzone (main.js → logoStep.
  // introSlot). Closed it invites the parade; open it MORPHS into "Design your own"
  // (Ryan 2026-07-17: "it gets replaced with a similar pill … that lives in the
  // left side-bar"), which leaves the parade and cues the dropzone. One element in
  // the pane means the parade needs NO bottom-band CTA to fight the product bar.
  const chipSpark = h('span', { class: 'wd-showcase-chip__spark', 'aria-hidden': 'true', html: SPARK_ICON });
  const chipLabel = h('span', { class: 'wd-showcase-chip__label' }, 'Show me what’s possible');
  const chip = h(
    'button',
    {
      type: 'button',
      class: 'wd-showcase-chip is-loading wd-flat-glass',
      hidden: true,
      'aria-label': 'Show me what’s possible — see example designs',
      /* ⛔ A TAP ON A SET THAT IS STILL BUILDING IS REMEMBERED, NOT DROPPED (Ryan 2026-08-12: *"I pick
         a new weight, I go back to Add Logo and click 'Show me what's possible' — the first click
         triggers the haptics but doesn't open the presentation. Always takes 2 clicks"*).
           Measured at that first tap: `{ active: false, ready: false }`. Switching product invalidates
         the rastered set, `refresh()` drops `ready` and starts a rebuild — and this handler's
         `else if (ready)` then evaluated to nothing at all. The haptics are the button doing its job;
         the tool simply had no answer. The second tap worked because the rebuild had landed by then.
           So the intent is latched and `preload` opens the parade on its first slide. The shimmer is
         back on the chip for the length of the rebuild (see `preload`), so the wait is stated rather
         than mysterious. */
      onClick: () => {
        if (active) { startDesign(); return; }
        if (ready) { enter(); return; }
        pendingEnter = true;
        if (!preloading) void preload();
      },
    },
    chipSpark,
    chipLabel,
  );

  /* Whether THIS parade is running as a phone presentation — the host says so by answering
     `onActiveChange` with a settle time (it is the only case with one). Used by `paintChip`. */
  let presentationMode = false;

  /** Reflect parade state onto the one pill: browse-invite vs. leave-and-design. */
  function paintChip() {
    /* ⛔ NOT IN PHONE PRESENTATION MODE (Ryan 2026-08-12: *"when you click 'Show me what's possible',
       as the menu starts sliding down, you see the bright blue button replace the 'Show me what's
       possible' button temporarily.. this should not happen on mobile"*).
         The morph is the DESKTOP exit — the pane pill becomes the way out. On a phone the sheet is
       leaving with the pill on it, and the real exit is the "Design your own" button between the
       arrows, so the swap buys nothing and the only thing it can do is be seen on the way down.
       Left as it was everywhere else. */
    const go = active && !presentationMode;
    chip.classList.toggle('wd-showcase-chip--go', go);
    chipLabel.textContent = go ? 'Design your own' : 'Show me what’s possible';
    chip.setAttribute(
      'aria-label',
      go ? 'Design your own — leave the examples' : 'Show me what’s possible — see example designs',
    );
  }

  // ── parade overlay (transparent — the plate shows through; controls at edges) ─
  // Name + dots ride a TOP header above the model now (Ryan 2026-07-17), off the
  // bottom band so nothing overlaps the product switcher bar. Dark on the light
  // studio stage, like the powered-by badge.
  const captionEl = h('div', { class: 'wd-showcase__caption' }, '');
  const dotsEl = h('div', { class: 'wd-showcase__dots', role: 'tablist', 'aria-label': 'Example designs' });
  const header = h('div', { class: 'wd-showcase__header' }, captionEl, dotsEl);
  const prevBtn = h('button', { type: 'button', class: 'wd-showcase__arrow wd-showcase__arrow--prev wd-flat-glass', 'aria-label': 'Previous example', onClick: () => prev() }, h('span', { 'aria-hidden': 'true', html: ARROW_ICON }));
  const nextBtn = h('button', { type: 'button', class: 'wd-showcase__arrow wd-showcase__arrow--next wd-flat-glass', 'aria-label': 'Next example', onClick: () => next() }, h('span', { 'aria-hidden': 'true', html: ARROW_ICON }));
  // No visible close button (Ryan 2026-07-16): dismiss via "Design your own" (the
  // pane pill), a logo upload, or Escape.
  /* ⛔ …EXCEPT ON A PHONE IN PRESENTATION MODE, WHERE THE PANE PILL IS OFF SCREEN (Ryan 2026-08-12).
   * Full presentation mode takes the sheet all the way down, so the chip that morphs into
   * "Design your own" goes with it — and the rule this file opens with is that the parade must never
   * be a screen with no door (`enter`'s own guard says so). This is that door, in the one place the
   * buyer is already looking: centred between the two arrows they have been tapping.
   *   Same `startDesign()` as the chip, so there is one exit behaviour rather than two. Rendered
   * always and revealed by CSS under `.wd-presenting` on phones — building it conditionally would
   * mean the overlay's contents depended on viewport at construction time, which a rotation breaks.
   */
  const goBtn = h('button', {
    type: 'button',
    class: 'wd-showcase__go wd-flat-glass',
    'aria-label': 'Design your own — leave the examples',
    onClick: () => startDesign(),
  }, 'Design your own');

  const overlay = h(
    'div',
    { class: 'wd-showcase', hidden: true, role: 'dialog', 'aria-label': 'Example designs', tabindex: '-1', onKeydown: onKey },
    header,
    prevBtn,
    nextBtn,
    goBtn,
  );

  /** "Design your own" — leave the parade, then cue the dropzone. */
  function startDesign() {
    /* ⛔ THE STEP CHANGE WAITS FOR THE SHEET, AND THAT IS THE WHOLE OF THE "SNAP" (Ryan 2026-08-12:
       *"when the 'Design your own' button is clicked, the bottom slide-up menu is no longer sliding
       up.. it just snaps up"*).
         `exit()` hands the sheet back its resting transform, and shell.css gives it a
       `transform 0.3s` transition to get there — so the rise should be free. It was not: the very
       next statement used to be `onStartDesign()`, which lands on `goToStep(…, { open: true })` →
       `easeAround`, and easeAround's FIRST act is `el.style.transition = 'none'` followed by a
       forced reflow. That reflow commits the pending transform change with transitions switched off,
       in the same tick, so the sheet teleports and no `transitionrun` ever fires (measured: 880 →
       462 in one frame, zero transition events).
         So the sheet's own beat runs first and the step change lands after it. `exit()` reports how
       long the chrome needs — the host owns that number, the same seam entry uses. One frame would
       not do: easeAround's pin does not merely fail to start a transition, it CANCELS one in flight.
       ⚠ Only this path waits. Escape, a logo upload and every other caller of `exit()` are unchanged;
       they have no follow-on work to collide with. */
    exit();
    // ⚠ CALLED IMMEDIATELY, and the HOST decides when to act on it: the thing that must be waited
    // for is the sheet's own `transitionend`, and the sheet is the host's element. A timer here was
    // measured landing 160ms inside a 300ms rise and cancelling it (`ctx.afterSheetSettles`).
    onStartDesign?.();
  }

  function onKey(e) {
    if (!active) return;
    if (e.key === 'Escape') { e.preventDefault(); exit(); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
  }

  /* ── WHICH CANVAS THE CUSTOMER IS LOOKING AT (2026-08-10) ─────────────────────────────────────
   * The parade rasters onto the LIVE stage, so the examples it can legitimately show depend on the
   * model currently loaded. `layoutFamilyFor` is the app's own product→family table (`plate` /
   * `grip` / `dumbbell`, the last of which the fixed barbell shares), read here rather than
   * restated so the two can never disagree.
   * ⚠ The BUILT set is stamped with the family it was built for, so `preload` can tell "already
   * loaded" from "loaded for a different product" — the distinction the old `ready` flag could not
   * make, and the reason a top-bar switch has to be able to rebuild. */
  const liveFamily = () => layoutFamilyFor(ctx.state?.ui?.product || '') || 'plate';
  /* ⛔ AND WHETHER THAT MODEL CAN BE DYED, WHICH IS NOT THE SAME QUESTION (Ryan 2026-08-10).
   * The competition and training bumpers are ONE family and only one of them dyes, so a set built
   * for the comp bumper is the right set for the training bumper and the WRONG picture: it carries
   * the buyer's pink, and its ink was peeled against pink. `productSupportsBodyColor` is the app's
   * own answer to "can this wear a colour" — asked here rather than restated.
   *   The BUILD KEY is therefore family + dyeability, because those are exactly the two things a
   * raster depends on. A key change means the built set belongs to the model we just left. */
  const liveDyeable = () => productSupportsBodyColor(ctx.state?.ui?.product || '');
  /* ⚠ THE KEY IS THE MODEL, NOT THE FAMILY — one step finer, deliberately. `buildRaster` lays the
   * design out with `modelFor(live product)`, and the dumbbell and the fixed barbell are one FAMILY
   * (they share a `capZone`) but two MODELS that `resolveZone`/`zoneWeight` treat separately. The
   * family still chooses which slides exist; the model is what the picture depends on, so it is the
   * model that decides when a rebuild is owed. */
  const buildKey = () => `${modelFor(ctx.state?.ui?.product || '') || 'plate'}|${liveDyeable() ? 'dye' : 'flat'}`;
  let builtKey = null;
  /* ⛔ THE SET IN HAND BELONGS TO A MODEL THE BUYER IS NO LONGER ON — asked at EVERY door into
   * showing, which is the whole lesson of this bug. 266 put the invalidation inside `preload`, and
   * `refresh` only calls `preload` `if (!ready)`. So the FIRST build latched `ready = true` and
   * nothing ever re-preloaded again: switch model with the parade closed, open it, and `enter`
   * showed the set rastered for whatever the tool booted on. Every one of Ryan's reports on
   * 2026-08-10 — the grip wearing the comp bumper's design, the dumbbell, the fixed barbell, and
   * the training bumper's pink printable face — was this one predicate not being asked. */
  const staleForLiveModel = () => !!builtKey && builtKey !== buildKey();

  /** One dot per built slide, active one marked. Rebuilt rather than appended-to because the set now
   *  GROWS while the parade may be open (see preload), and a rebuild cannot drift from `items` the
   *  way an incremental append can. ≤22 buttons; the cost is nothing next to the raster that
   *  triggered it. */
  function syncDots() {
    if (!items) { dotsEl.replaceChildren(); return; }
    dotsEl.replaceChildren(
      ...items.map((it, i) =>
        h('button', {
          type: 'button', class: 'wd-showcase__dot', role: 'tab',
          'aria-label': it.name, onClick: () => show(i, {}),
        }),
      ),
    );
    if (active) paintHeader(index); // keep the current slide's dot lit through the rebuild
  }

  // ── preload ────────────────────────────────────────────────────────────────
  async function preload() {
    /* ⛔ A PRODUCT SWITCH INVALIDATES THE BUILD, and `ready` alone said otherwise. The rasters are
       drawn for one family; when the buyer switches the top bar to a dumbbell, the bumper examples
       are no longer the right ones to be showing — and after the family gate they may not even be
       the right COUNT. Dropping the flags here is what lets the same call rebuild. */
    if (staleForLiveModel()) {
      ready = false; preloading = false; items = null;
      /* …AND THE CHIP SAYS SO AGAIN. A rebuild is the same wait as the first build, on a chip that
         is already sitting there looking live — without the shimmer back, a tap during it is a
         button that does nothing for no stated reason (Ryan 2026-08-12: *"the first click triggers
         the haptics but doesn't open the presentation"*). */
      chip.classList.add('is-loading');
    }
    if (preloading || ready) return;
    // Captured OUTSIDE the try so the catch can record the attempt against it — see there.
    const key = buildKey();
    /* …and so can the catch: with the chip going live on the FIRST slide, a throw part-way through
       the set is no longer "the parade failed". It is a parade with fewer slides in it, already on
       screen, and hiding the chip out from under it would be the worse answer. */
    const built = [];
    preloading = true;
    try {
      /* The designs are DATA now, fetched from rfq-worker, not four identities compiled into this
         bundle (Ryan 2026-07-29 — admins publish them from the portal). Same shape back, so
         everything below this line is untouched: an empty list throws, which is caught below and
         hides the chip — the behaviour that used to mean "crest assets failed" and now also means
         "nobody has published an example yet". */
      const mod = await import('../showcase/remote.js');
      // Captured BEFORE the await, and stamped on the build below — a switch mid-fetch must not
      // leave a bumper set labelled as a dumbbell one and thereby become un-rebuildable.
      const family = liveFamily();
      /* ⭐ ONE SLIDE AT A TIME, RASTERED AS IT LANDS. `onSlide` fires between entry downloads (each of
         which is megabytes), so the first plate is on screen while the second is still on the wire —
         the difference between "clickable in a couple of seconds" and "clickable when the whole
         published set has been fetched AND rendered". Returning `false` stops the walk. */
      await mod.loadShowcaseSlides({ family, onSlide: async (d) => {
        /* ⚠ ONCE THE CHIP IS LIVE, THE REST OF THE BUILD GIVES WAY. Every raster below is a full-size
           render on the main thread, and from here on it can land in the middle of the entrance
           set-piece or a flip — the buyer can now open the parade before the set is finished, which
           is the whole point of the change. `requestIdleCallback` hands those frames back; the 600ms
           timeout keeps a busy tool from starving the build entirely. Nothing waits before the FIRST
           slide: that one is what everybody is waiting on. */
        if (ready) {
          /* ⛔ AND IT STANDS ALL THE WAY DOWN WHILE THE CHROME IS MOVING. `requestIdleCallback` alone
             was not enough: it yields until the thread is idle, and then hands back a slot in which
             this starts a 200-300ms synchronous raster — which is fine at rest and ruinous during
             the 300ms the sheet is sliding. Measured on the way OUT of presentation mode: the sheet's
             transform transition did not even START until 277ms after the tap, and was then cancelled
             mid-flight. A background nicety must not be able to eat an animation the buyer is
             watching, so `quietUntil` blocks the whole beat, not just the frame. */
          const wait = quietUntil - (typeof performance !== 'undefined' ? performance.now() : 0);
          // eslint-disable-next-line no-await-in-loop
          if (wait > 0) await new Promise((r) => setTimeout(r, wait));
          await new Promise((r) => {
            if (typeof requestIdleCallback === 'function') requestIdleCallback(() => r(), { timeout: 600 });
            else setTimeout(r, active ? 250 : 0);
          });
        }
        /* ⛔ THE BUILD ABANDONS ITSELF IF THE BUYER MOVES. Now that this loop keeps running after the
           chip has gone live, it can still be rastering when a product switch invalidates everything
           it is making. `buildKey()` is the same predicate `staleForLiveModel` asks; checked per
           iteration because each one is hundreds of ms. Without it the tail of a bumper set would
           append itself to a dumbbell's parade, one slide at a time. */
        if (buildKey() !== key || retired) return false;
        /* ⛔ REBUILD THE LOGO COMPOSITE FIRST (Ryan 2026-07-31).
           `logo.renderUrl` — the recoloured / peeled / ringed raster — is a TRANSIENT that
           `serializeLogo` deliberately drops, so `buildVariantProfile` above restores every slide
           with it null and `render-engine` falls back to `logo.dataUrl`: the ORIGINAL artwork.
           A published example whose logo was recoloured has therefore been showing the wrong
           colours in the parade since the feed replaced the compiled-in identities — silently,
           because the plate still renders and only the mark is wrong.

           Found while fixing the identical hole in `plate-batch` (WD 178), where it reached a
           customer's packet. Same cure, and it belongs HERE rather than in `showcase/remote.js`:
           that module is a pure reader with no ctx, and this is the line that rasters.

           ⚠ AFTER `applyLayoutPreset`, which `buildVariantProfile` has already run — a layout
           change re-slots the logo element, so compositing before it would rebuild the wrong
           element's raster. */
        await ctx.rebuildLogoComposite?.(d.profile);
        // Sequential so we never contend the raster canvas / stall the main thread.
        /* ⚠ THE ZONE AND THE GROUND COLOUR ARE `buildRaster`'s JOB, NOT THIS LOOP'S. Both depend on
           the LIVE model rather than on the slide, and `buildRaster` already reads it — the same
           two options the buyer's own texture is built with. Deriving them a second time here is
           how the two drift (project CLAUDE.md §0b); this loop's only job is which design.
             What stays here is `dyeable`, and only as part of the BUILD KEY — "does this raster
           need rebuilding", which is a different question from "how is it drawn". */
        const raster = await ctx.stage.buildRaster(d.profile, d.weight);
        if (!raster) return true;
        // The stage tint is gated by `productSupportsBodyColor` at the door (ctx.stage.show), so a
        // model that cannot be dyed ignores whatever an example stored.
        built.push({ id: d.id, name: d.name, caption: d.caption, raster, bodyColor: d.profile.bodyColor });
        /* ⭐ THE CHIP GOES LIVE ON THE FIRST SLIDE, NOT THE LAST (Ryan 2026-08-12: *"how come, on page
           load when starting a new project, there's always lag for the 'Show me what's possible'
           button to be clickable? The font is sort of gray for a few seconds until it turns white"*).
             This loop rasters EVERY published example before it enabled anything — 22 of them today,
           sequentially, because each one is a full-size render and running them in parallel would
           contend the raster canvas. That is the grey pill and the repeating shimmer sweep he was
           watching: measured at 33–46s on a phone against the live feed, and it grows every time an
           admin publishes. Nothing about it was ever needed to OPEN the parade — entry shows slide 0.
             So: publish after the first raster and keep building behind it. The dots grow as slides
           land, which is also the honest picture of what is happening.
             ⚠ `items` and `built` are the SAME ARRAY from here on, deliberately — every later push
           extends the live set, and `syncDots` is what makes it visible. `show()` takes its index
           modulo `items.length` on each call, so a set that grows under an open parade is fine; what
           would NOT be fine is a reader caching the length, and none does.
             ⚠ The rest of the loop is untouched — same order, same sequencing, same `buildRaster`.
           The only change is WHEN the tool starts saying yes. */
        if (built.length === 1) {
          items = built;
          builtKey = key;   // what this set is FOR — see the guard at the top of preload()
          ready = true;
          chip.classList.remove('is-loading'); // stop the shimmer — the chip is live
          resolveReady?.();
          // Anyone who already asked for the parade has waited long enough — one slide is a parade.
          if (pendingEnter) { pendingEnter = false; if (!retired && !active) enter(); }
        }
        syncDots();
        return true;
      } });
      if (!built.length) throw new Error('no showcase rasters');
    } catch (err) {
      // Assets failed → the chip simply never appears (§3.1 error state).
      // eslint-disable-next-line no-console
      console.error('[weight-designer] showcase preload failed:', err);
      // …unless slides are already up. See `built`'s declaration: a failure at slide 9 of 22 leaves a
      // working 8-slide parade, and the chip is its only door.
      if (!built.length) chip.hidden = true;
      /* ⛔ RECORD THE ATTEMPT EVEN WHEN IT FAILS, or "stale" never clears and every caller that
         re-preloads on staleness retries forever. A set that covers the bumper only genuinely has
         nothing for a dumbbell — that is an answer, not a reason to ask again. A real switch moves
         the key and asks properly. */
      builtKey = key;
    } finally {
      preloading = false;
    }
  }

  // ── parade control ───────────────────────────────────────────────────────────
  function enter() {
    // ⛔ NEVER ENTER WITHOUT AN EXIT (Ryan 2026-08-03). The parade has no close button of its own
    // (see the note above the chip): the ONLY way out is the chip morphing to "Design your own".
    // `retire()` hides that chip permanently the moment a buyer commits a logo, so entering after
    // it is a screen with no door — which is exactly what an admin "Preview" button did before it
    // was rebuilt as an in-dialog contact sheet. The guard belongs here, at the trap, not at the
    // caller, because any future caller would rebuild it.
    if (retired) return;
    /* ⛔ AND NEVER ENTER ON A SET BUILT FOR ANOTHER MODEL. This is the door Ryan actually used —
       switch the top bar with the parade CLOSED, then press the chip — and it had no invalidation
       at all, so it opened whatever was rastered first. Rebuild, then enter; the re-entry re-checks
       rather than assuming, because the buyer can switch again while it builds. */
    if (staleForLiveModel()) {
      if (!preloading) {
        /* ⛔ RE-ENTER ON THE FIRST SLIDE, NOT ON THE WHOLE REBUILD (Ryan 2026-08-12: *"I pick a new
           weight, I go back to Add Logo and click 'Show me what's possible' — the first click
           triggers the haptics but doesn't open the presentation. Always takes 2 clicks"*).
             A product switch invalidates the set, so the first tap lands here and starts a rebuild.
           This used to wait on `preload()`'s own promise, which resolves when the LAST slide has
           rastered — 45s before 284's streaming, and still the whole set after it. The buyer taps
           again in the meantime, by then `ready` is true, and the second tap works: two clicks, with
           the first one apparently doing nothing.
             `pendingEnter` is honoured at the same moment the chip goes live, so the tap opens the
           parade as soon as there is one plate to show. It is the SAME latch the chip's own handler
           sets for a tap that lands before anything is ready — one mechanism, two doors into it. A
           model with nothing to show never reaches it, and so cannot bounce between here and there.
           ⚠ NOT also on the promise: a rebuild now runs for ~45s in the background, and re-entering
           at the END of it would drop a buyer who had already left back into the parade. */
        pendingEnter = true;
        void preload();
      }
      return;
    }
    if (!ready || !items || active) return;
    active = true;
    // The entrance owns the next beat: chrome out, plate down-and-grow, then the set-piece. Nothing
    // background-built may land in the middle of it.
    holdBuild(1200);
    overlay.classList.remove('is-exiting'); // clear a leftover exit anim if re-entered fast
    overlay.hidden = false;
    // Slide the tool cluster out of the corner (the 'x' takes it). It answers with how long the
    // STAGE needs before a set-piece should land on it — see `settleMs` below.
    const settleMs = Number(onActiveChange?.(true)) || 0;
    // ⚠ AFTER the host has answered, because its answer is what tells the pill whether to morph at
    // all — a phone presentation leaves the label alone (see `paintChip`). Painting first, as this
    // did, is exactly how the blue button got a frame on screen.
    presentationMode = settleMs > 0;
    paintChip(); // desktop: the pane pill morphs to "Design your own" and STAYS visible
    // Focus the overlay so arrow-keys / Esc work immediately.
    try { overlay.focus({ preventScroll: true }); } catch { /* older browsers */ }
    // Kick the dramatic 3D reveal one frame LATER so the corner swap (cluster out +
    // 'x'/arrows in) gets its opening frames on the compositor before the heavy WebGL
    // spin-in renders. The controls animate transform/opacity only, so once started
    // they ride the GPU and the spin can't stutter them (Ryan 2026-07-16).
    const dramatic = !ctx.stage.prefersReducedMotion;
    /* ⭐ …OR SEVERAL HUNDRED MILLISECONDS LATER, WHEN THE STAGE IS STILL MOVING (Ryan 2026-08-12:
       *"it spins — bad, as it should enlarge to be full width first… then only once it's done
       sliding down and gotten its smooth width dims set, then it can spin"*).
         On a phone, entering presentation mode re-frames the camera as the sheet leaves: the plate
       slides down and grows over `settleMs`. A reveal OWNS the camera, so a spin started into that
       swallowed the growth and applied it in one step at the end — the snap he saw. The host is the
       one that knows the duration, so it says so and this waits; one motion, then the set-piece.
         The header is painted NOW rather than with the slide, so the name and dots arrive with the
       arrows instead of appearing 700ms into an empty frame. */
    if (dramatic && settleMs) {
      paintHeader(0);
      window.setTimeout(() => { if (active) show(0, { dramatic }); }, settleMs);
    } else if (dramatic && typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => { if (active) show(0, { dramatic }); });
    } else {
      show(0, { dramatic });
    }
  }

  /** The header half of a slide — who is on stage. Split out so `enter` can put the name and dots
   *  up with the rest of the parade chrome while the stage is still re-framing under them. */
  function paintHeader(i) {
    if (!items || !items[i]) return;
    // NAME only (Ryan 2026-07-16) — the "— school colors, Varsity Arch" descriptor is
    // gone from the tool; keep it out of the on-stage chrome. (`caption` still exists
    // on the identity for a11y announce below.)
    captionEl.textContent = items[i].name;
    Array.from(dotsEl.children).forEach((d, k) => {
      const on = k === i;
      d.classList.toggle('is-active', on);
      d.setAttribute('aria-selected', on ? 'true' : 'false');
    });
  }

  function show(i, opts) {
    if (!items) return;
    index = ((i % items.length) + items.length) % items.length;
    ctx.stage.show(items[index].raster, { ...(opts || {}), bodyColor: items[index].bodyColor });
    paintHeader(index);
    // Announced HERE and not in `paintHeader`: the announcement is "this design is on the plate",
    // which is true when the stage has it, not when its name went up.
    ctx.announce?.(`Showing: ${items[index].name}`);
  }

  const next = () => show(index + 1, {});
  const prev = () => show(index - 1, {});

  /** @returns {number} ms the chrome needs to come back — see `startDesign`. 0 when nothing moves. */
  function exit() {
    if (!active) return 0;
    active = false;
    presentationMode = false;
    holdBuild(900); // …and the way out owns it too — the sheet's rise is the thing being protected
    paintChip(); // pane pill morphs back to "Show me what's possible"
    // …and it answers with how long the sheet takes to rise, for the one caller that has follow-on
    // work capable of interrupting it.
    const settleMs = Number(onActiveChange?.(false)) || 0;
    ctx.announce?.('Left the examples — back to your blank plate.');
    // Reduced motion: no slide — restore + hide at once.
    if (ctx.stage.prefersReducedMotion) {
      overlay.hidden = true;
      ctx.stage.restore();
      refresh();
      return 0;
    }
    // Start EVERY parade control sliding out first (compositor: transform/opacity),
    // and only THEN — a couple of frames later — do the heavy plate rebuild. Running
    // restore() up front blocked the main thread exactly as the close-out began,
    // which is the lag Ryan saw; deferring it lets the slide-out own its opening
    // frames while the rebuild happens underneath, invisibly (Ryan 2026-07-16).
    overlay.classList.add('is-exiting');
    const finishRestore = () => { ctx.stage.restore(); refresh(); };
    if (settleMs > 0) {
      /* ⛔ AND ON A PHONE IT WAITS FOR THE SHEET, because "a couple of frames later" is not late
         enough for THIS rebuild. `restore()` rasters the buyer's own design at full size — 2048² on
         a phone — and that is hundreds of milliseconds of synchronous main-thread work landing
         exactly on top of the sheet's 300ms rise. A transform transition on a glass panel with a
         backdrop-filter is not free of the main thread, so the block does not merely stutter it: the
         sheet was measured sitting at its down position for ~800ms and then arriving in one step,
         with the transition CANCELLED part-way (by the step change that follows).
           The example plate stays on the model for those 300ms, shrinking up with the sheet, and
         crossfades back to the blank plate once the chrome has landed. That is the same order the
         eye reads on the way IN — the chrome moves, then the picture changes. */
      if (typeof ctx.afterSheetSettles === 'function') ctx.afterSheetSettles(finishRestore, settleMs + 600);
      else window.setTimeout(finishRestore, settleMs);
    } else if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => requestAnimationFrame(finishRestore));
    } else {
      finishRestore();
    }
    // Unmount the overlay after the slide-out completes. `!active` lets a fast
    // re-enter win over this timeout.
    /* ⛔ HIDDEN WHEN THE SLIDE-OUT ENDS, NOT ON A 300ms CLOCK. The timer started when the tap was
       handled; the animations start when the style change is PROCESSED, which on a busy thread is
       later — measured 336ms in, so a 300ms hide cancelled all three controls mid-flight
       (`animationcancel` on the arrows and on "Design your own"). An event cannot be early.
       ⚠ `animationend` BUBBLES, so one listener on the overlay covers every control; the names all
       end in `-out…`, and the three run together, so the first end is the end of the beat. The
       timeout stays as the floor for a browser that never runs them at all. */
    const finishHide = () => {
      overlay.removeEventListener('animationend', onOutEnd);
      window.clearTimeout(hideTimer);
      if (!active) { overlay.hidden = true; overlay.classList.remove('is-exiting'); }
    };
    const onOutEnd = (e) => { if (/-out/.test(e.animationName || '')) finishHide(); };
    overlay.addEventListener('animationend', onOutEnd);
    const hideTimer = window.setTimeout(finishHide, 900);
    return settleMs;
  }

  // ── visibility / lifecycle ───────────────────────────────────────────────────
  // The chip belongs to the blank-but-armed intro only: gone once a logo lands or
  // a design is restored, and while the parade itself is open.
  function isIntro() {
    const ui = ctx.state && ctx.state.ui;
    return !!ui && !ui.hasLogo && !ui.isDemo;
  }

  function refresh() {
    paintChip();
    // Retired for good → never re-invite (see retire()). Wins over every other state.
    if (retired) { chip.hidden = true; return; }
    // Parade open → the pill is the visible "Design your own" exit (in the pane).
    if (active) { chip.hidden = false; return; }
    if (!stageReady || !isIntro()) { chip.hidden = true; return; }
    chip.hidden = false;
    // ⚠ `!ready` ALONE WAS THE BUG (see `staleForLiveModel`). A set built for the model the buyer
    // has just left is as good as no set at all, so it has to re-preload on the same terms.
    if ((!ready || staleForLiveModel()) && !preloading) void preload();
  }

  // Retire the chip FOR GOOD (Ryan 2026-07-18): once the buyer commits their OWN logo,
  // they've chosen to design — don't pull them back to "what's possible" mid-flow. A
  // latch refresh() honours (unlike dismiss(), which only fades and can be re-shown, and
  // bailed while the parade was open). Tears the parade down too, and fades the chip out.
  // Idempotent. Called at logo-commit (handleFile), long before the late `hasLogo` flip.
  let retired = false;
  function retire() {
    if (retired) return;
    retired = true;
    if (active) exit(); // the parade overlay leaves with it
    if (!chip.hidden) {
      chip.classList.add('is-dismissing');
      window.setTimeout(() => { chip.hidden = true; chip.classList.remove('is-dismissing'); }, 340);
    } else {
      chip.hidden = true;
    }
  }

  // Called by main once the 3D stage is live (WebGL supported + pipeline warm).
  // In the WebGL-fallback path this is never called, so the chip never appears.
  function onStageReady() {
    stageReady = true;
    refresh();
  }

  // Fade the intro chip out for good — called the moment the design process begins
  // (the buyer's logo gets dressed onto the plate). hasLogo already gates refresh(),
  // so once it's gone it stays gone; this just makes the exit a graceful fade rather
  // than the instant hide refresh() would do (Ryan 2026-07-16).
  function dismiss() {
    if (chip.hidden || active) return;
    chip.classList.add('is-dismissing');
    window.setTimeout(() => { chip.hidden = true; chip.classList.remove('is-dismissing'); }, 340);
  }

  // Re-paint the CURRENT identity onto the stage — used when the live product changes
  // WHILE the parade is open (top-bar switch), which rebuilds the reveal from the user's
  // real (blank) design and would otherwise drop the hypothetical to a blank plate
  // (Ryan 2026-07-16). Non-dramatic: just push the same raster onto the new product.
  const isActive = () => active;

  /* ⛔ A SWITCH WHILE THE PARADE IS OPEN NEEDS A REBUILD, NOT A RE-PUSH (Ryan 2026-08-10:
   * *"basically everything is getting the comp bumper design, instead of its correct respective
   * design — grip plate, dumbbells, training bumper, fixed barbell"*).
   *
   * 🔑 266 GAVE `preload` THE INVALIDATION AND THIS IS THE PATH THAT ACTUALLY FIRES. A top-bar
   * switch rebuilds the reveal from the buyer's own (blank) design, and `reapply` exists to put the
   * hypothetical back — but it re-pushed `items[index].raster`, which was rastered for the model
   * being LEFT. `preload`'s guard never ran, because a live switch does not call `preload`. So the
   * tool boots on a comp bumper, the parade builds for it, and every model the buyer switches to
   * afterwards was handed the bumper's picture. The per-slide family gate in showcase/remote.js
   * could not help: the right slide was never re-selected, because nothing re-selected anything.
   *
   * ⚠ THE STAGE STAYS ON THE BUYER'S BLANK PLATE WHILE IT REBUILDS, deliberately. The alternative
   * is showing the outgoing model's arrangement for the second the rasters take — which is the bug
   * itself, just briefer.
   * ⛔ AND IF THE NEW MODEL HAS NOTHING TO SHOW, LEAVE. A set that covers the bumper only leaves a
   * dumbbell with zero slides, and sitting in an open parade with no plate and arrows that step
   * through nothing is a room with no door (memory: never enter a screen whose exit is
   * conditional). `exit()` restores the buyer's own design and puts the chip back.
   */
  async function rebuildForLiveModel() {
    await preload();                      // invalidates on the key change, then rebuilds
    if (!active) return;                  // they left while it was building
    if (!ready || !items || !items.length) { exit(); return; }
    index = 0;
    show(0, {});                          // a switch is a crossfade, never the entrance spin
  }

  const reapply = () => {
    if (!active) return;
    if (staleForLiveModel()) { void rebuildForLiveModel(); return; }
    if (items) ctx.stage.show(items[index].raster, { bodyColor: items[index].bodyColor });
  };

  // `exit` is exported so the logo step can tear the parade down the instant the
  // buyer commits their OWN logo (dropzone click / upload / drop) — the pane pill
  // and Escape were the only exits before, so starting a real design left the
  // overlay (renderings, arrows, top label) stuck on the stage (Ryan 2026-07-18).
  // It self-guards on `active`, so calling it when the parade is closed is a no-op.
  return { chipEl: chip, overlayEl: overlay, refresh, onStageReady, dismiss, retire, whenReady: () => readyPromise, enter, exit, isActive, reapply };
}

const SPARK_ICON =
  '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">' +
  '<path d="M12 2l1.9 5.1L19 9l-5.1 1.9L12 16l-1.9-5.1L5 9l5.1-1.9z"/><path d="M19 14l.9 2.4L22 17l-2.1.6L19 20l-.9-2.4L16 17l2.1-.6z"/></svg>';
const ARROW_ICON =
  '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>';
